import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import Button from 'tio-ui/components/button';
import Input from 'tio-ui/components/input';
import type { ErrorsSignature } from 'tio-ui/components/error-messages';
import CategorizedUpload from 'tio-common/components/tuition-assistance/forms/categorized-upload';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import { collectApplicationAssets } from 'tio-employee/utils/tas/application-assets';
import { getAllValuesForTasField } from 'tio-common/utils/tuition-assistance/fields';
import { inputsForFields } from 'tio-common/utils/data/inputs-for-fields';
import SubmitEvidenceCourseRow from './submit-evidence-course-row';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';
import type PartnerService from 'tio-employee/services/partner';

export interface SubmitEvidenceBundle {
  onSaveGrade: (course: TasCourseModel, grade: string) => Promise<void>;
  onSaveGpa: (gpa: number) => Promise<void>;
  onAcceptConditions: (conditions: ConditionModel[]) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError: string | ErrorsSignature | null;
  canSubmit: boolean;
}

interface SubmitEvidenceBodySignature {
  Args: {
    application: TasApplicationModel;
    /** Optional so isolated component tests can render this task without
     * wiring the full ManagePage-owned bundle; every real render passes one. */
    evidence?: SubmitEvidenceBundle;
  };
  Element: HTMLDivElement;
}

const EVIDENCE_CLASSIFICATION = 'COMPLETION_DOCUMENTS';

/**
 * Collapsible body of the "Submit your Evidence Items" task: per-course grade
 * entry, the GPA field, and document upload — the checklist an employee opens
 * the card to work through. Mirrors the legacy show page's grade modal +
 * attachments section (see show.gts), inline instead of routing out to the
 * guided wizard.
 *
 * The condition agreements and the submit action are NOT here. They live in
 * `SubmitEvidenceActions`, rendered in the task card's always-visible footer so
 * the terms cannot be hidden behind a collapsed card — see that component and
 * the `footer` block in task-card.gts.
 *
 * `@evidence` is still the whole ManagePage-owned bundle rather than the two
 * grade callbacks this component uses, because both halves of the card are
 * driven by one bundle and splitting it would put its shape in two places.
 */
export default class SubmitEvidenceBody extends Component<SubmitEvidenceBodySignature> {
  @service declare partner: PartnerService;
  @tracked pendingGpa = '';

  get application() {
    return this.args.application;
  }

  get programTemplate() {
    return this.application.tasProgramInstance?.tasProgramTemplate;
  }

  get activeCourses() {
    return this.application.activeCourses ?? [];
  }

  get grades() {
    const exclusiveGradeOptions = (getAllValuesForTasField(
      'EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS',
      this.programTemplate?.fields
    ) ?? []) as string[];
    if (exclusiveGradeOptions.length > 0) {
      return exclusiveGradeOptions;
    }
    return inputsForFields['COURSE_GRADE'].options.map((option) => option.value);
  }

  // Company capability, not a per-browser beta opt-in — same rule as the
  // application show page (#9889): AI summaries not disabled for the company,
  // and the template configures document categories.
  get useCategorizedUpload() {
    if (this.partner.companySettings?.tuitionAssistance?.isAiSummariesEnabled === false) {
      return false;
    }
    const entries = this.programTemplate?.documentEntriesFor(EVIDENCE_CLASSIFICATION) ?? [];
    return entries.length > 0;
  }

  get isAttachmentRequired() {
    return this.application.isEvidenceRequired;
  }

  /** Everything attached to the application at any level — feeds
   * CategorizedUpload's "Previously uploaded" history and the free-form
   * list, exactly as the legacy page passes its `allAttachments` union.
   * Without it, documents from earlier approval steps were invisible on
   * this surface (#10393). */
  get allRelatedAssets() {
    return collectApplicationAssets(this.application);
  }

  get showGPAField() {
    const typeClassification = this.application.typeClassification;
    return Boolean(
      this.programTemplate?.calculateTotalByGPA &&
      (typeClassification === 'TAS.ProgramType.3' || typeClassification === 'TAS.ProgramType.4.3')
    );
  }

  updateGpa = (value: string) => {
    this.pendingGpa = value;
  };

  saveGpa = dropTask(async () => {
    if (!this.pendingGpa.trim() || !this.args.evidence) {
      return;
    }
    await this.args.evidence.onSaveGpa(Number(this.pendingGpa));
  });

  <template>
    <div class="tio-col gap-4" ...attributes>
      {{#if this.activeCourses.length}}
        <div class="tio-col gap-2">
          {{#each this.activeCourses as |course|}}
            <SubmitEvidenceCourseRow
              @course={{course}}
              @grades={{this.grades}}
              @onSave={{@evidence.onSaveGrade}}
            />
          {{/each}}
        </div>
      {{/if}}

      {{#if this.showGPAField}}
        <div class="tio-col gap-2 rounded-lg tio-bg-sunken p-3">
          <label for="evidence-gpa" class="tio-text-sm-semibold tio-fg-primary">
            {{t "tas.application.grade_point_average"}}
          </label>
          <div class="tio-row items-center gap-2">
            <Input
              @size="lg"
              type="number"
              min="0"
              max="4.0"
              step="0.01"
              id="evidence-gpa"
              data-test-evidence-gpa-input
              @value={{this.pendingGpa}}
              class="w-40"
              @onChange={{this.updateGpa}}
            />
            <Button
              @intent="primary"
              @isDisabled={{not this.pendingGpa}}
              @isRunning={{this.saveGpa.isRunning}}
              data-test-evidence-gpa-cta
              {{on "click" this.saveGpa.perform}}
            >
              {{t "common.save"}}
            </Button>
          </div>
        </div>
      {{/if}}

      {{#if this.isAttachmentRequired}}
        <div class="tio-col gap-2">
          <p class="tio-text-sm-semibold tio-fg-primary">
            {{t "tuition_assistance.applications.manage.tasks.upload_documents_label"}}
          </p>
          {{#if this.useCategorizedUpload}}
            {{! @relatedAssets feeds the "Previously uploaded" history —
              earlier steps' documents, view-only — the legacy page renders
              from its own allAttachments union (#10393). }}
            <CategorizedUpload
              @programTemplate={{this.programTemplate}}
              @classification={{EVIDENCE_CLASSIFICATION}}
              @attachable={{this.application}}
              @attachableType="tasApplication"
              @existingAssets={{this.application.tasAssets}}
              @relatedAssets={{this.allRelatedAssets}}
            />
          {{else}}
            {{! The legacy page's free-form list also renders the full union,
              not just application-level assets (#10393). }}
            <UploadAttachments
              @programTemplate={{this.programTemplate}}
              @classification={{EVIDENCE_CLASSIFICATION}}
              @attachable={{this.application}}
              @attachableType="tasApplication"
              @useDropzone={{true}}
              @attachmentListItems={{this.allRelatedAssets}}
              @hideDiscarded={{true}}
            />
          {{/if}}
        </div>
      {{/if}}

      {{! The terms and the Submit button are NOT here — they render in the task
        card's footer, outside the collapsible region, so an employee who never
        expands the card still sees what they are agreeing to. See
        submit-evidence-actions.gts. }}
    </div>
  </template>
}
